import { OPERATOR_SYMBOL_TO_SYMPY_RELATION_MAP } from "../mappings";

import { extractBoundVarName, op, SympyBuildError } from "./shared";

import type { SympyBuilder } from "./SympyBuilder";
import type { SemanticExpr } from "../types";
import type { NodeJsonMap } from "@corca/data";

export function parseSumProduct(
  builder: SympyBuilder,
  node: NodeJsonMap["function"],
  sympyFunc: "Sum" | "Product",
): SemanticExpr {
  const symbolNode = node.children.find((n) => n.type === "symbol");
  const bottomLabelNode = symbolNode?.children.find(
    (n) => n.type === "label" && n.props.position === "right-bottom",
  );
  const topLabelNode = symbolNode?.children.find(
    (n) => n.type === "label" && n.props.position === "right-top",
  );
  const bottomLabelInline = bottomLabelNode?.children.find((n) => n.type === "inline");
  const topLabelInline = topLabelNode?.children.find((n) => n.type === "inline");

  const bodyNode = node.children.find((n) => n.type === "inline");
  if (!bodyNode) {
    throw new SympyBuildError(`Empty ${sympyFunc} body`);
  }

  const boundVarName = extractBoundVarName(bottomLabelInline);
  let pushedBoundVar = false;
  if (boundVarName && !builder.boundVarsScope.has(boundVarName)) {
    builder.boundVarsScope.add(boundVarName);
    pushedBoundVar = true;
  }

  let bodyExpr: SemanticExpr | null;
  try {
    bodyExpr = builder.traverseNode(bodyNode, 2);
  } finally {
    if (pushedBoundVar && boundVarName) builder.boundVarsScope.delete(boundVarName);
  }
  if (bodyExpr === null) {
    throw new SympyBuildError(`Empty ${sympyFunc} body`);
  }

  if (!bottomLabelInline) {
    throw new SympyBuildError(`${sympyFunc} requires at least a variable in the bottom label`);
  }

  let varExpr: SemanticExpr | null = null;
  let startExpr: SemanticExpr | null = null;

  // The bottom label is typically `<index> = <expr>`
  const eqIndex = bottomLabelInline.children.findIndex(
    (n) =>
      n.type === "operatorSymbol" &&
      n.children[0]?.type === "symbol" &&
      OPERATOR_SYMBOL_TO_SYMPY_RELATION_MAP[n.children[0].props.key ?? ""] === "Equality",
  );

  if (eqIndex !== -1) {
    varExpr = builder.foldInlineChildren(bottomLabelInline.children.slice(0, eqIndex), 2);
    startExpr = builder.foldInlineChildren(bottomLabelInline.children.slice(eqIndex + 1), 2);
  } else {
    // No `=`: the whole bottom label is either the index variable or a bare start value.
    const bottomNode = builder.traverseNode(bottomLabelInline, 2);
    const bottomExpr = bottomNode === null ? "" : builder.emitInline(bottomNode).trim();
    if ([...builder.variables.values()].includes(bottomExpr)) {
      varExpr = builder.traverseNode(bottomLabelInline, 2);
    } else {
      startExpr = builder.traverseNode(bottomLabelInline, 2);
      if (builder.variables.size > 0) {
        varExpr = { type: "symbol", name: builder.variables.keys().next().value! };
      } else {
        builder.variables.set("i", `var${builder.variables.size}`);
        varExpr = { type: "symbol", name: "i" };
      }
    }
  }

  if (!varExpr) {
    throw new SympyBuildError(`Invalid ${sympyFunc} variable`);
  }

  // SymPy serializes Sum/Product limits as a bare tuple → JSON array; mirror that (no Tuple op).
  if (startExpr && topLabelInline) {
    const endExpr = builder.traverseNode(topLabelInline, 2);
    if (endExpr === null) {
      throw new SympyBuildError(`${sympyFunc} requires upper limit`);
    }
    return op(sympyFunc, [bodyExpr, [varExpr, startExpr, endExpr]]);
  }
  if (startExpr) {
    throw new SympyBuildError(`${sympyFunc} requires upper limit`);
  }
  return op(sympyFunc, [bodyExpr, varExpr]);
}
